<template>
	<GxzzProductListContent
		v-if="isGxzz"
		:initial-company-id="companyId"
		:website-info="websiteInfo?.data || {}"
	/>
	<div v-else class="search-page" @click="closeMenu">
		<div class="top-head">
			<div class="left-slot" @click.stop>
				<van-icon name="arrow-left" size="1.5rem" color="#000000" @click="goBack" />
			</div>
			<div class="title-row">{{ titles || '按分类查找' }}</div>
			<div class="right-slot" @click.stop>
				<div class="right-actions">
					<i class="iconfont icon-fuwuhaoxiaochengxutubiao_fangdajing search-icon" @click="toSearch"></i>
					<i class="iconfont icon-gongneng_sandiangengduo more-icon" @click.stop="toggleMenu"></i>
				</div>
				<DropDown class="dialogBox" :show="showMenu" @close="closeMenu" />
			</div>
		</div>

		<PaginationList
			ref="paginationListRef"
			:fetch-data="fetchProductList"
			:page-size="10"
			:margin-bottom="0"
			enableRefresh
			:scroll-container-style="{ height: 'calc(100vh - 3.67rem)' }"
		>
			<template #list="{ list }">
				<SmallImg :data-list="list" fromPage="商品搜索列表" />
			</template>
		</PaginationList>
	</div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { showToast } from 'vant';
import PaginationList from '@/components/core/PaginationList.vue';
import SmallImg from '@/components/SmallImg.vue';
import DropDown from '@/components/DropDown.vue';
import { getMobileCategoryFromSearch, getGoodsPricePre, getMutil, queryWebsiteByCompanyId } from '@/api/index.js';
import { getDiscountList } from '@/api/goods.js';
import { getCompanyInfo } from '@/common/common.js';
import { isGxzzFromWebsiteData } from '@/utils/business/siteGxzz.js';
import { mergeProductPriceFields } from '@/utils/productPrice';
import { getStorageSync, setStorageSync } from '@/utils/storage';
import GxzzProductListContent from '../gxzz/products/components/GxzzProductListContent.vue';

const route = useRoute();
const companyId = computed(() => route.params.companyId || route.query.companyId || '');
const { data: websiteInfo } = await useAsyncData(
	() => `website-search-${companyId.value || 'none'}`,
	() => (companyId.value ? queryWebsiteByCompanyId(companyId.value) : Promise.resolve(null)),
	{ watch: [companyId] }
);
const isGxzz = computed(() => isGxzzFromWebsiteData(websiteInfo.value?.data));
const titles = computed(() => String(route.query.keyword || route.query.searchName || '').trim());
const firstcategory = computed(() => route.query.firstcategory || 'null');
const secondcategory = computed(() => route.query.secondcategory || 'null');
const thirdcategory = computed(() => route.query.thirdcategory || 'null');
const productnature = computed(() => route.query.productnature || 'null');
const hotWordsId = computed(() => route.query.hotWordsId || '');

const paginationListRef = ref(null);
const companyCode = ref('');
const isLogin = ref(false);
const showMenu = ref(false);

const queryParams = reactive({
	filterConditions: [],
	companyCode: '',
	searchName: '',
	pageNum: 1,
	pageSize: 10,
	companyId: '',
	orderBy: '',
	orderByType: '',
	ranking: '',
});

const getAllGoodsPrice = async list => {
	const arrayPrice = list.map(item => item.productid || item.productId).filter(Boolean);
	if (!arrayPrice.length) return list;
	const hasAccessToken = import.meta.client ? !!getStorageSync('cmdc_access_token_mobile') : !!isLogin.value;
	if (!hasAccessToken) return list;
	const res = await getGoodsPricePre(arrayPrice);
	if (res.code == 200) {
		res.data.forEach(item => {
			list.forEach(item1 => {
				if ((item1.productid || item1.productId) === (item.productid || item.productId)) {
					item1.price = item.price ?? item1.price;
					// 补价接口字段名不固定，这里同步 referencePrice/referenceprice 与 priceType/pricetype。
					mergeProductPriceFields(item1, item);
					item1.iscontrolsales = item.iscontrolsales;
					item1.issee = item.issee;
				}
			});
		});
	}
	return list;
};

const fetchProductList = async (pageNum, pageSize) => {
	const companyInfo = await getCompanyInfo(null, null, companyId.value);
	companyCode.value = companyInfo?.companyCode || '';
	Object.assign(queryParams, {
		filterConditions: [],
		companyCode: companyCode.value || '',
		searchName: titles.value || '',
		pageNum,
		pageSize,
		companyId: companyId.value || '',
		orderBy: '',
		orderByType: '',
		ranking: '',
	});
	if (String(queryParams.companyId) === '1') {
		delete queryParams.companyId;
	}
	if (hotWordsId.value) {
		queryParams.hotWordsId = hotWordsId.value;
		delete queryParams.searchName;
	} else {
		delete queryParams.hotWordsId;
	}
	if (secondcategory.value !== 'null') {
		queryParams.filterConditions.push({ key: 'secondcategory', value: secondcategory.value });
	}
	if (thirdcategory.value !== 'null') {
		queryParams.filterConditions.push({ key: 'thirdcategory', value: thirdcategory.value });
	}
	if (productnature.value !== 'null') {
		queryParams.productnature = productnature.value;
	} else {
		delete queryParams.productnature;
	}

	const res = await getMobileCategoryFromSearch({ ...queryParams });
	if (String(res?.code) !== '200') {
		throw new Error(res?.message || '获取列表失败');
	}

	const mutilList = await getMutil({ f: true });
	if (import.meta.client) {
		setStorageSync('getMutilAll', mutilList.data);
	}

	const listProductIds = [];
	res.data.list.forEach(item => {
		listProductIds.push({
			productCode: item.productcode || item.productCode,
			productId: item.productid,
			companyCode: item.filialecode,
		});
		const site = mutilList.data.find(x => x.companyCode == item.filialecode);
		if (site) {
			item.companyId = site.companyId;
			item.websiteName = site.websiteName;
			item.companyName = site.companyName;
		}
		item.defaultImageUrl = item.defaultimageurl || item.defaultImageUrl;
		item.productId = item.productId || item.productid;
	});

	const resCoupons = await getDiscountList(listProductIds);
	if (String(resCoupons?.code) === '200') {
		res.data.list.forEach(item => {
			resCoupons.data.forEach(coupon => {
				if (item.productid === coupon.productId) {
					item.zero = coupon.zero;
					item.coupon = coupon.coupon;
					if (coupon.activity) item.activityType = coupon.activity.activityType;
				}
			});
		});
	}

	let arr = res.data.list || [];
	if (isLogin.value) {
		arr = await getAllGoodsPrice(arr);
	}
	return arr;
};

const goBack = () => {
	history.go(-1);
};

const toSearch = () => {
	showMenu.value = false;
	navigateTo({
		path: '/category/search',
		query: {
			...route.query,
			companyId: companyId.value || '',
			firstcategory: firstcategory.value,
			secondcategory: secondcategory.value,
			thirdcategory: thirdcategory.value,
			productnature: productnature.value,
		},
	});
};

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
};

const closeMenu = () => {
	showMenu.value = false;
};

onMounted(async () => {
	isLogin.value = !!getStorageSync('cmdc_access_token_mobile');
	const info = await getCompanyInfo(null, null, companyId.value);
	companyCode.value = info?.companyCode || '';
	queryParams.companyCode = companyCode.value || '';
	if (!companyId.value) {
		showToast('缺少站点参数');
	}
});

useSeoMeta({
	title: '国械在线-按分类查找',
	ogTitle: '国械在线-按分类查找',
	ogImage: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
});
</script>

<style scoped lang="scss">
.search-page {
	min-height: 100vh;
	background: #f7f8fa;
}

.top-head {
	display: flex;
	height: 3.67rem;
	align-items: center;
	justify-content: space-between;
	padding: 0 0.7rem;
	background: #fff;
	font-size: 1.17rem;
	box-sizing: border-box;
	position: sticky;
	top: 0;
	z-index: 10;
	border-bottom: 1px solid #ebebeb;
}

.left-slot {
	flex: 1;
	display: flex;
	align-items: center;
	min-width: 0;
}

.title-row {
	flex: 2;
	min-width: 0;
	text-align: center;
	font-size: 1.17rem;
	font-weight: 500;
	line-height: 1.83rem;
	color: #333333;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
}

.right-slot {
	flex: 1;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	min-width: 0;
	position: relative;
}

.right-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
}

.search-icon {
	font-size: 1.33rem;
	color: #000000;
	/* 与旧 topBar .solts 一致 */
	margin-right: 1.25rem;
}

.more-icon {
	font-size: 1.5rem;
	color: #000000;
	/* 与旧 topBar .icon-gongneng_sandiangengduo 一致 */
	margin-right: 1rem;
}

/* 旧项目 m-mall-dc topBar：.dialogBox { top: 3rem; right: 1rem; margin-top: 0.4rem }，三角 right: 1.15rem、top: -0.3rem。
 * 下拉挂在右侧 flex 槽内，相对整屏会偏左下；略减小 top/right 实现「上移、靠右」对齐三点 */
.dialogBox {
	position: absolute;
	top: 2.65rem;
	right: 0.35rem;
	width: 9.58rem;
	background: #25293c;
	z-index: 30;
	margin-top: 0.25rem;
	border-radius: 0.42rem;

	&:before {
		position: absolute;
		right: 0.85rem;
		top: -0.3rem;
		content: '';
		display: block;
		width: 0.94rem;
		height: 0.94rem;
		background-color: #25293c;
		border-radius: 0.25rem;
		transform: rotate(45deg);
	}
}
</style>
